import { css } from "@emotion/react";
import { useCallback, useMemo, useState } from "react";

import { BetaMultiTypeahead } from "@zapier/design-system";

export type Item = { disabled?: boolean } & Record<keyof any, unknown>;

type MultiselectDropdownProps<T> = {
  /**
   * Accessible and visible label for the field.
   */
  label: string;
  /**
   * The list of items.
   */
  items: T[];
  /**
   * The currently selected items.
   */
  selectedItems?: T[];
  /**
   * Called when the selection changes.
   */
  onChange: (items: T[]) => void;
  /**
   * Returns the display label for an item.
   */
  getLabelForItem?: (item?: T) => string;
  /**
   * Returns a unique string key for an item.
   */
  getKeyForItem?: (item: T, index?: number) => string;
  /**
   * Placeholder text shown when nothing is selected.
   */
  placeholder?: string;
  /**
   * Disables the input.
   */
  isDisabled?: boolean;
  /**
   * Marks the field as invalid.
   */
  isErrored?: boolean;
  /**
   * Renders an icon for each item in the dropdown.
   */
  renderIcon?: (item: T) => React.ReactNode;
  /**
   * Max number of items visible before the menu scrolls.
   * Each item is approximately 44px tall.
   */
  visibleItems?: number;
  /**
   * Size of the input field.
   * @default "compact"
   */
  size?: "compact" | "medium" | "large";
};

const ITEM_HEIGHT_PX = 44;

// Visually hide the label while keeping it accessible to screen readers.
// BetaMultiTypeahead always renders a visible <label> when the label prop is
// set. In filter-toolbar contexts we want the accessible name without the
// extra vertical space it adds.
const srOnlyLabelStyle = css`
  & label {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

function MultiselectDropdown<T extends Item>({
  label,
  items,
  selectedItems = [],
  onChange,
  getLabelForItem = (item: any) => item?.label ?? "",
  getKeyForItem = (item: any) => String(item?.id ?? ""),
  placeholder,
  isDisabled = false,
  isErrored,
  renderIcon,
  visibleItems,
  size = "compact",
}: MultiselectDropdownProps<T>) {
  const [inputValue, setInputValue] = useState("");

  // Build id→item and item→id lookups together with the adapted list.
  // Capturing itemToId lets adaptedSelected and fullItemById reuse the exact
  // id that was assigned when each item was indexed here — including cases
  // where getKeyForItem uses the `index` parameter. Without this, a selected
  // item at position 0 in selectedItems but position 5 in items would receive
  // a different key and the selection would fail to match.
  const { itemById, itemToId, adaptedItems } = useMemo(() => {
    const byId = new Map<string, T>();
    const toId = new Map<T, string>();
    const adapted = items.map((item, index) => {
      const id = String(getKeyForItem(item, index));
      byId.set(id, item);
      toId.set(item, id);
      return { id, name: getLabelForItem(item) };
    });
    return { itemById: byId, itemToId: toId, adaptedItems: adapted };
  }, [items, getKeyForItem, getLabelForItem]);

  // Extend the map with any selected items that aren't in the base list
  // (e.g. pre-selected values loaded before the full list arrives).
  // Use itemToId to get the correct id for items that are in the current list.
  const fullItemById = useMemo(() => {
    let extended = false;
    const map = new Map(itemById);
    selectedItems.forEach((item, index) => {
      const id = itemToId.get(item) ?? String(getKeyForItem(item, index));
      if (!map.has(id)) {
        map.set(id, item);
        extended = true;
      }
    });
    // Return the original map when nothing was added to preserve reference
    // stability and avoid invalidating useCallback deps that read fullItemById.
    return extended ? map : itemById;
  }, [itemById, itemToId, selectedItems, getKeyForItem]);

  const adaptedSelected = useMemo(
    () =>
      selectedItems.map((item, index) => ({
        // Use the id assigned during items indexing when available, so
        // index-based getKeyForItem implementations stay consistent.
        id: itemToId.get(item) ?? String(getKeyForItem(item, index)),
        name: getLabelForItem(item),
      })),
    [selectedItems, itemToId, getKeyForItem, getLabelForItem]
  );

  const handleSelectedItemsChange = useCallback(
    (adapted: { id: string | number; name: string }[]) => {
      const original = adapted
        .map(({ id }) => fullItemById.get(String(id)))
        .filter((item): item is T => item !== undefined);
      onChange(original);
    },
    [fullItemById, onChange]
  );

  const adaptedRenderIcon = useMemo(
    () =>
      renderIcon
        ? ({ item }: { item: { id: string | number; name: string } }) => {
            const original = fullItemById.get(String(item.id));
            return original ? renderIcon(original) : null;
          }
        : undefined,
    [renderIcon, fullItemById]
  );

  const menuHeight = visibleItems
    ? `${(visibleItems + 0.5) * ITEM_HEIGHT_PX}px`
    : undefined;

  return (
    <div css={srOnlyLabelStyle}>
      <BetaMultiTypeahead
        label={label}
        items={adaptedItems}
        selectedItems={adaptedSelected}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        onSelectedItemsChange={handleSelectedItemsChange}
        placeholder={placeholder}
        isDisabled={isDisabled || items.length === 0}
        isInvalid={isErrored}
        size={size}
        renderIcon={adaptedRenderIcon}
        menuHeight={menuHeight}
      />
    </div>
  );
}

export default MultiselectDropdown;
